/**
 * Mass Role - Format Pillar
 *
 * Formata valores de massa para apresentação.
 *
 * @example
 * import { formatMass } from '@attrx/role-morphic/mass/format';
 *
 * formatMass('kilogram', 75.5);                      // "75.5 kg"
 * formatMass('kilogram', 75.5, { verbose: true });   // "75.5 kilograms"
 * formatMass('pound', 1000, { locale: 'en-US' });    // "1,000 lb"
 */

import { MASS_UNITS, type MassUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface MassFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de massa para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatMass('kilogram', 75.5);                        // "75.5 kg"
 * formatMass('kilogram', 75.5, { verbose: true });     // "75.5 kilograms"
 * formatMass('kilogram', 1, { verbose: true });        // "1 kilogram"
 * formatMass('pound', 1000, { locale: 'en-US' });      // "1,000 lb"
 * formatMass('gram', 1500, { decimals: 0 });           // "1500 g"
 */
export function formatMass(
  variant: MassUnit,
  value: number,
  options: MassFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = MASS_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown mass variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getMassSymbol('kilogram');  // "kg"
 * getMassSymbol('pound');     // "lb"
 */
export function getMassSymbol(variant: MassUnit): string {
  const unit = MASS_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown mass variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getMassName('kilogram', 1);   // "kilogram"
 * getMassName('kilogram', 2);   // "kilograms"
 */
export function getMassName(variant: MassUnit, count: number = 1): string {
  const unit = MASS_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown mass variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
